import React, { useState, useEffect, useRef } from 'react';
import { getTextColorForBackground } from '../utils/colorUtils';
import CatSVG from '../utils/CatSVG';
import DogSVG from '../utils/DogSVG';
import PandaSVG from '../utils/PandaSVG';

const avatars = [
  { id: 1, name: "Gato", component: CatSVG },
  { id: 2, name: "Perro", component: DogSVG },
  { id: 3, name: "Panda", component: PandaSVG },
];

const colors = ["#ff9aa2", "#ffdac1", "#b5ead7", "#c7ceea", "#f0d9ff", "#a1c4fd"];

const defaultParticipant = {
  id: 1,
  name: "Jugador 1",
  avatar: avatars[0],
  color: colors[0],
};

const ParticipantSelector = ({ onParticipantsChange }) => {
  const [participants, setParticipants] = useState([defaultParticipant]);
  const [editingColor, setEditingColor] = useState(null);
  const participantsContainerRef = useRef(null); 

  useEffect(() => {
    // Actualizar el estado de los participantes al montarse el componente
    onParticipantsChange(participants);
    // Solo al montar: incluir participants u onParticipantsChange volvería a
    // notificar al padre en cada cambio, que no es lo que se busca aquí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Se ejecuta una vez al montarse

  const handleAddParticipant = () => {
    const newId = participants.length + 1;
    const newParticipant = {
      id: newId,
      name: `Jugador ${newId}`,
      avatar: avatars[newId % avatars.length],
      color: colors[newId % colors.length],
    };
    const updatedParticipants = [...participants, newParticipant];
    setParticipants(updatedParticipants);
    onParticipantsChange(updatedParticipants);

    // Desplazar hacia el final cuando se agrega un nuevo participante
    setTimeout(() => {
      participantsContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleRemoveParticipant = (id) => {
    const updatedParticipants = participants.filter(p => p.id !== id);
    setParticipants(updatedParticipants);
    onParticipantsChange(updatedParticipants);
  };

  const handleNameChange = (id, newName) => {
    const updatedParticipants = participants.map(p => (p.id === id ? { ...p, name: newName } : p));
    setParticipants(updatedParticipants);
    onParticipantsChange(updatedParticipants);
  };

  const handleToggleColorPicker = (id) => {
    setEditingColor(editingColor === id ? null : id);
  };

  const handleColorChange = (id, newColor) => {
    const updatedParticipants = participants.map(p => (p.id === id ? { ...p, color: newColor } : p));
    setParticipants(updatedParticipants);
    onParticipantsChange(updatedParticipants);
    setEditingColor(null);
  };

  return (
    <div className="container p-4" style={{ paddingBottom: '60px', overflowY: 'auto' }}>
      <h2 className="text-5xl font-extrabold mb-4 text-center text-primary">
        ¡Es hora de reunir a tu equipo!
      </h2>
      <p className="text-center text-primary text-2xl mb-6">
        Añade a tus compañeros y personalízalos a tu gusto.
      </p>

      <div
        ref={participantsContainerRef} 
        className={`gap-4 ${participants.length === 1 ? 'flex' : 'grid grid-cols-1 sm:grid-cols-2 mb-5'}`}
        style={{ width: participants.length === 1 ? '100%' : 'auto' }}
      >
        {participants.map((participant, index) => {
          const AvatarComponent = participant.avatar.component;
          return (
            <div key={participant.id} className="relative card flex flex-col items-center p-4 rounded-lg shadow-lg">
              {index > 0 && (
                <span
                  onClick={() => handleRemoveParticipant(participant.id)}
                  className="absolute top-2 right-4 text-lg cursor-pointer"
                  aria-label={`Eliminar a ${participant.name}`}
                >
                  &#10005;
                </span>
              )}
              <AvatarComponent color={participant.color} className="w-24 h-24" />
              <input
                type="text"
                value={participant.name}
                onChange={(e) => handleNameChange(participant.id, e.target.value)}
                onFocus={(e) => e.target.value = ''}  // Borra el contenido cuando el input recibe foco
                className="mt-2 p-2 rounded-lg text-center focus:outline-none focus:ring-2"
                aria-label={`Nombre del participante ${participant.id}`}
                placeholder="Escribe el nombre del jugador"
              />

              <label className="flex items-center mt-2">
                <button
                  className="w-8 h-8 rounded-full focus:outline-none mr-2"
                  style={{ backgroundColor: participant.color }}
                  aria-label={`Color actual ${participant.color}`}
                  disabled
                />
                <span
                  onClick={() => handleToggleColorPicker(participant.id)}
                  className="flex items-center cursor-pointer ml-2"
                  aria-label="Editar color"
                >
                  🎨 
                  {participants.length === 1 && index === 0 && (
                    <span className="ml-2">elige tu color</span>
                  )}
                </span>
              </label>

              {editingColor === participant.id && (
                <div className="flex flex-wrap justify-center gap-2 mt-2 -mb-2">
                  {colors.map((color) => {
                    const colorTextColor = getTextColorForBackground(color);
                    return (
                      <button
                        key={color}
                        onClick={() => handleColorChange(participant.id, color)}
                        style={{ backgroundColor: color, color: colorTextColor }}
                        className={`w-8 h-8 rounded-full focus:outline-none ${participant.color === color ? 'ring-2 ring-primary-color' : ''}`}
                        aria-label={`Seleccionar color ${color}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {participants.length < 5 && (
        <div className="w-full">
          <button
            onClick={handleAddParticipant}
            className="opacity-70 flex items-center justify-center p-4 rounded-lg shadow-lg text-center w-full mb-10 mt-5"
            aria-label="Añadir un nuevo participante"
          >
            <span className="text-xl font-bold">+ añade refuerzos</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ParticipantSelector;
